/**
 * Application Providers
 *
 * Centralized provider composition following the "Provider Pattern".
 * Order matters - providers are nested from outer to inner.
 *
 * Provider Order:
 * 1. ErrorBoundary - Catch unhandled errors
 * 2. QueryClientProvider - Server state management
 * 3. ThemeProvider - MUI theme & CSS baseline
 * 4. BrowserRouter - Client-side routing
 * 5. AuthInitializer - Initialize auth state
 * 6. SocketIOProvider - Real-time socket connection
 * 7. Suspense - Loading states for lazy components
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Suspense, useEffect, useSyncExternalStore } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter } from 'react-router-dom';

import { AuthInitializer } from '@/modules/auth';
import { useAuthStore } from '@/modules/auth/application/store';
import { AuthStatus } from '@/modules/auth/domain/types';
import { userKeys } from '@/modules/user/application';
import { WebrtcInitializer } from '@/modules/webrtc';
import { useWorkTimeStore } from '@/modules/workTime/application/store';
import { AppErrorFallback, LoadingScreen, ToastProvider } from '@/shared/components';
import { SocketIOProvider } from '@/shared/services/socketio';
import { ThemeProvider } from '@/shared/theme';

import type { ReactNode } from 'react';

// ============================================================================
// Query Client Configuration
// ============================================================================

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

// ============================================================================
// Auth Token Hook (for SocketIO Connection)
// ============================================================================

/**
 * Subscribe to auth token changes in localStorage.
 * This ensures socket reconnects when user logs in/out.
 */
function useAuthToken(): string | null {
  return useSyncExternalStore(
    // Subscribe to storage events
    callback => {
      window.addEventListener('storage', callback);
      window.addEventListener('auth-change', callback);
      return () => {
        window.removeEventListener('storage', callback);
        window.removeEventListener('auth-change', callback);
      };
    },
    // Get current snapshot
    () => localStorage.getItem('auth_token'),
    // Server snapshot (SSR)
    () => null
  );
}

// ============================================================================
// Providers Component
// ============================================================================

interface AppProvidersProps {
  children: ReactNode;
}

export function AppProviders({ children }: AppProvidersProps) {
  const authToken = useAuthToken();
  const authStatus = useAuthStore((state) => state.status);
  const clearWorkTime = useWorkTimeStore((state) => state.clearWorkTime);

  useEffect(() => {
    if (authStatus === AuthStatus.Unauthenticated) {
      queryClient.removeQueries({ queryKey: userKeys.all });
      clearWorkTime();
    }
  }, [authStatus, clearWorkTime]);

  return (
    <ErrorBoundary FallbackComponent={AppErrorFallback} onReset={() => window.location.reload()}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <ToastProvider>
            <BrowserRouter>
              <AuthInitializer>
                <WebrtcInitializer />
                <SocketIOProvider authToken={authToken}>
                  <Suspense fallback={<LoadingScreen />}>{children}</Suspense>
                </SocketIOProvider>
              </AuthInitializer>
            </BrowserRouter>
          </ToastProvider>
        </ThemeProvider>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

// ============================================================================
// Auth Helper (call this when user logs in/out)
// ============================================================================

/**
 * Helper to notify providers of auth changes.
 * Call this after setting/clearing the auth token.
 *
 * @example
 * ```ts
 * // On login
 * localStorage.setItem('auth_token', token);
 * notifyAuthChange();
 *
 * // On logout
 * localStorage.removeItem('auth_token');
 * notifyAuthChange();
 * ```
 */
export function notifyAuthChange(): void {
  window.dispatchEvent(new Event('auth-change'));
}
